import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, HStack, ChevronRightIcon, Box } from 'native-base';
import { range } from 'lodash';

import { Text } from '@pimm/base';
import { BadgeCalendarEvent, useCalendarEvents } from '@app/features/calendar';
import { DashboardModalType, useDashboard } from '@app/features/dashboard';
import { ResourceLoader } from '@app/components/shared';
import { useSiteTime } from '@app/features/store-core';
import { WidgetBox, WidgetBoxProps } from '../widget-box';
import { useAppLocale } from '@pimm/common';

const MAX_EVENTS = 8;

export const WidgetCelebrations = ({ ...props }: WidgetBoxProps) => {
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const siteTime = useSiteTime();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { toggleModal } = useDashboard();
  const [currentDate, setCurrentDate] = useState<Date>();
  const { events: allEvents, getEventsByDate } = useCalendarEvents();

  const eventsForDate = useMemo(() => {
    return currentDate ? getEventsByDate(currentDate) : [];
  }, [allEvents, currentDate]);

  useEffect(() => {
    const refreshCurrentDate = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      const day = siteTime.toStartEndOfBlock();

      if (day.startTime && day.endTime) {
        setCurrentDate(day.startTime);

        const now = siteTime.today();
        const delay = day.endTime.getTime() - now.getTime();

        timeoutRef.current = setTimeout(() => refreshCurrentDate(), delay);
      }
    };

    refreshCurrentDate();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [siteTime.toStartEndOfBlock]);

  return (
    <WidgetBox hideTitle p={1} {...props}>
      <HStack alignItems="center" justifyContent="space-between" px={1} h={7}>
        <Box flex={1}>
          <Text
            size="sm"
            fontWeight={700}
            color="gray.600"
            lineHeight="md"
            textTransform="uppercase"
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {translate(props?.title, props?.titleTranslations)}
          </Text>
        </Box>
      </HStack>

      <ResourceLoader
        minH="90px"
        flex={1}
        pt={0.5}
        px={2}
        pb={3}
        isEmpty={eventsForDate.length === 0}
        emptyMessage={t('widgets:no_events_today')}
        _text={{ size: 'sm' }}
      >
        <HStack flexWrap="wrap">
          {range(MAX_EVENTS).map(index => {
            const event = eventsForDate[index];
            const isLast = index === MAX_EVENTS - 1;
            const isMore = isLast && eventsForDate.length > MAX_EVENTS;

            if (!currentDate || !event) return null;

            if (!isMore && event) {
              return (
                <Box key={`event.${index}`} w="1/2" p={0.5}>
                  <BadgeCalendarEvent key={`event.${index}`} date={currentDate} event={event} rounded="md" minH={6} />
                </Box>
              );
            }

            return (
              <Box w="1/2" p={0.5}>
                <Pressable onPress={() => toggleModal({ type: DashboardModalType.EmployeeCelebration })}>
                  <HStack
                    space={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                    pl={1.5}
                    pr={0.5}
                    h={5}
                    bgColor="gray.50"
                    rounded="sm"
                  >
                    <Text size="sm" color="gray.900" lineHeight="sm">
                      +{eventsForDate.length - index} {t('calendar:see_more')}
                    </Text>
                    <ChevronRightIcon size={3} color="gray.900" />
                  </HStack>
                </Pressable>
              </Box>
            );
          })}
        </HStack>
      </ResourceLoader>
    </WidgetBox>
  );
};
